import type { FC, ReactNode } from "react"
import { useRef, useState, useLayoutEffect, useCallback, useEffect } from "react"

import { createPortal } from "react-dom"

function createWrapperAndAppendToBody(id: string): HTMLElement {
  const wrapper = document.createElement(`div`)
  wrapper.setAttribute(`id`, id)
  document.body.appendChild(wrapper)

  return wrapper
}

const useWrapper = (elementId: string): HTMLElement | null => {
  const [element, setElement] = useState<HTMLElement | null>(null)

  useLayoutEffect(() => {
    let el = document.getElementById(elementId)
    let systemCreated = false

    if (!el) {
      systemCreated = true
      el = createWrapperAndAppendToBody(elementId)
    }
    setElement(el)

    return () => {
      if (systemCreated && el?.parentNode) {
        el.parentNode.removeChild(el)
      }
    }
  }, [elementId])

  return element
}

export type PortalProps = {
  children: ReactNode
}

export const useNewPortal =
  (elementId: string, visible: boolean): FC<PortalProps> =>
  ({ children }) => {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    const wrapper = useWrapper(elementId)

    return visible && wrapper ? createPortal(children, wrapper) : null
  }

export interface UsePortalProps {
  elementId: string
  initial?: boolean
  onHide?: VoidFunction
  onShow?: VoidFunction
}

export interface UsePortalReturn {
  Portal: FC<PortalProps>
  visible: boolean
  show: () => void
  hide: () => void
  toggle: () => void
}

export interface UsePortal {
  (props: UsePortalProps): UsePortalReturn
}

export const usePortal: UsePortal = ({
  elementId,
  initial = false,
  onShow,
  onHide,
}) => {
  const [visible, setVisible] = useState(initial)

  const watchRef = useRef<boolean>(initial)

  const show = useCallback(() => {
    if (watchRef.current) return

    if (onShow) onShow()

    setVisible(true)
    watchRef.current = true
  }, [onShow])

  const hide = useCallback(() => {
    if (!watchRef.current) return

    if (onHide) onHide()

    setVisible(false)
    watchRef.current = false
  }, [onHide])

  const toggle = useCallback(() => {
    if (watchRef.current) {
      hide()
    } else {
      show()
    }
  }, [show, hide])

  const Portal = useCallback(useNewPortal(elementId, visible), [
    visible,
    elementId,
  ])

  return { Portal, visible, toggle, show, hide }
}

export interface UseCoordinatesProps {
  elementId: string
  leftMargin?: number
  topMargin?: number
  dir?: `above` | `under`
  scrollId?: string
}

export interface UseCoordinatesReturn {
  coords: {
    left: number
    top: number
    width: number
    height: number
  }
}

export const useCoordinates = ({
  elementId,
  dir,
  topMargin = 0,
  leftMargin = 0,
  scrollId,
}: UseCoordinatesProps): UseCoordinatesReturn => {
  const [coords, setCoords] = useState({ left: 0, top: 0, width: 0, height: 0 })
  useEffect(() => {
    const handleScroll = () => {
      const el = document.getElementById(elementId)

      if (el) {
        const rect = el.getBoundingClientRect()
        setCoords({
          left: rect.left + leftMargin,
          top: rect.top + topMargin + rect.height * +(dir === `under`),
          width: rect.width,
          height: rect.height,
        })
      }
    }
    const scrollElement = scrollId ? document.getElementById(scrollId) : null

    if (scrollElement) {
      scrollElement.addEventListener(`scroll`, handleScroll)
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener(`scroll`, handleScroll)
      }
    }
  }, [])

  useEffect(() => {
    const handleScroll = () => {
      const el = document.getElementById(elementId)
      if (el) {
        const rect = el.getBoundingClientRect()
        setCoords({
          left: rect.left + leftMargin,
          top: rect.top + topMargin + rect.height * +(dir === `under`),
          width: rect.width,
          height: rect.height,
        })
      }
    }
    const scrollElement = scrollId ? document.getElementById(scrollId) : window
    if (scrollElement) {
      scrollElement.addEventListener(`scroll`, handleScroll)
    }

    return () => {
      if (scrollElement) {
        scrollElement.removeEventListener(`scroll`, handleScroll)
      }
    }
  }, [])

  useLayoutEffect(() => {
    const el = document.getElementById(elementId)
    if (el) {
      const rect = el.getBoundingClientRect()
      setCoords({
        left: rect.left + leftMargin,
        top: rect.top + topMargin + rect.height * +(dir === `under`),
        width: rect.width,
        height: rect.height,
      })
    }
  }, [elementId])

  return { coords }
}
